import { Button, Icon, Input, Text, HStack, VStack } from '@genial/components';
import { Color, Spacing, Typography } from '@genial/design-tokens';
import { CostSimulatorEntity, ErrorListResponse, Service } from '@genial/services';
import { Utils } from '@genial/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box } from 'native-base';
import React, { useState } from 'react';
import { Control, useForm, useWatch } from 'react-hook-form';
import { Pressable } from 'react-native';
import { useRecoilValue } from 'recoil';

import { useToastCreator } from '../../../../../../hooks/useToastCreator';
import { useOrderActions } from '../../../../../../store/order/actions';
import { orderItemState } from '../../../../../../store/order/atom';
import { hasOrderItem } from '../../../../../../store/order/selectors';
import { useProductActions } from '../../../../../../store/product/actions';
import { structuredOperationProductSelectedState } from '../../../../../../store/product/atom';
import { screenName } from '../../../../utils';
import { appliedAmountFormData, validationInvestmentSchema } from '../../constants';
import { InvestmentCostSectionProps } from '../StructuredOperationSubDetailsSection';

export const StructuredOperationCostSimulatorSection: React.FC<InvestmentCostSectionProps> = ({
  marketIsOpened,
  costSimulator,
  minValue = 0,
  onClose,
}) => {
  const [costSimuatorData, setCostSimulatorData] = useState<CostSimulatorEntity>(
    costSimulator || {}
  );
  const [isLoadingCostSimulator, setIsLoadingCostSimulator] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const [prevValue, setPrevValue] = useState(Utils.Mask.Money(minValue));
  const { showToastFromError } = useToastCreator('cost-simulator-error-toast');
  const { handleSelectOrderItem, updateOrderStatus } = useOrderActions();
  const { handleSelectProductItem, updateProductStatus } = useProductActions();
  const orderItem = useRecoilValue(orderItemState);
  const hasOrder = useRecoilValue(hasOrderItem);
  const productItem = useRecoilValue(structuredOperationProductSelectedState);

  const {
    control,
    formState: { errors, isValid },
    resetField,
    handleSubmit,
  } = useForm<appliedAmountFormData>({
    mode: 'all',
    reValidateMode: 'onBlur',
    resolver: zodResolver(validationInvestmentSchema(minValue)),
    defaultValues: { appliedAmount: Utils.Mask.Money(costSimulator?.valueSimulation) },
  });

  const currentAppliedAmount = useWatch({
    control,
    name: 'appliedAmount',
  });

  const iconPropsFocused = {
    name: 'Close',
    size: 'xxs',
    color: Color['brand.primary.principal'],
    variant: 'rounded',
    backgroundColor: Color['neutral.light'],
  } as const;

  const iconPropsUnfocused = {
    name: 'EditPen',
    size: 'xxxs',
    color: Color['brand.tertiary.principal'],
  } as const;

  const appliedAmountIsChanged = prevValue !== Utils.Mask.InputMoney(currentAppliedAmount);

  const iconProps =
    Utils.Mask.Money(minValue) !== Utils.Mask.InputMoney(currentAppliedAmount)
      ? iconPropsFocused
      : iconPropsUnfocused;

  const hasErrorInput = errors?.appliedAmount;

  const handleCostSimulator = async ({ appliedAmount }: appliedAmountFormData) => {
    if (!appliedAmountIsChanged) {
      setIsFocused(false);
      return;
    }
    try {
      setPrevValue(Utils.Mask.InputMoney(appliedAmount));
      setIsLoadingCostSimulator(true);

      const amount = Utils.Unmask.Money(appliedAmount);
      const { data } = await Service.COES.V1.getOffersByCostSimulatorProductIdAmount({
        amount,
        productId: productItem!.productId!,
        screenName,
      });

      setCostSimulatorData(data.costSimulator || {});
    } catch (error) {
      (error as ErrorListResponse).errors?.forEach((errorItem) => showToastFromError(errorItem));
    } finally {
      setIsLoadingCostSimulator(false);
      setIsFocused(false);
    }
  };

  const handleSubmitItem = ({ appliedAmount }: appliedAmountFormData) => {
    onClose && onClose();
    const value = Utils.Unmask.Money(appliedAmount);
    handleSelectProductItem({ ...productItem, appliedAmount: value, type: 'StructuredOperations' });
    updateProductStatus({ label: 'Produto selecionado', status: 'completed' });
    updateOrderStatus('opened');

    if (hasOrder) {
      handleSelectOrderItem({ name: orderItem.name, description: orderItem.description });
      updateOrderStatus('completed');
    }
  };

  return (
    <VStack space={Spacing['inline.xxxs']}>
      <Box py={Spacing['inline.xxxs']}>
        <Input
          onFocus={() => setIsFocused(true)}
          onBlur={() => {
            if (isValid) {
              handleCostSimulator({ appliedAmount: currentAppliedAmount });
            }
          }}
          keyboardType="numeric"
          testId={'modal-structured-operations-details-input-application-amount'}
          onSubmitEditing={handleSubmit(handleCostSimulator)}
          name={'appliedAmount'}
          control={control as unknown as Control}
          autoComplete="off"
          hasShadow={false}
          label="Valor do investimento"
          variant="underlined"
          mask={Utils.Mask.InputMoney}
          isDisabled={isLoadingCostSimulator}
          placeholder={Utils.Mask.Money(minValue)}
          inputRightElement={
            <Pressable
              onPress={() => {
                if (iconProps.name === 'Close') {
                  resetField('appliedAmount');
                  setCostSimulatorData(costSimulator || {});
                  setPrevValue(Utils.Mask.Money(minValue));
                }
              }}>
              <Icon testID="qa" {...iconProps} />
            </Pressable>
          }
          {...(hasErrorInput && { error: hasErrorInput })}
        />
      </Box>
      <VStack space={Spacing['inline.xxxs']}>
        <HStack alignItems={'center'} justifyContent={'space-between'}>
          <Text color={'neutral.black'} variant="regular" fontSize={Typography['fontsize.md']}>
            Corretagem de entrada
          </Text>
          <Text
            color={'neutral.black'}
            variant="regular"
            isLoading={isLoadingCostSimulator}
            fontSize={Typography['fontsize.md']}
            {...(isLoadingCostSimulator && {
              maxW: '80px',
            })}>
            {Utils.Mask.Money(costSimuatorData?.brokerageEntry || 0)}
          </Text>
        </HStack>
        <HStack alignItems={'center'} justifyContent={'space-between'}>
          <Text color={'neutral.black'} variant="regular" fontSize={Typography['fontsize.md']}>
            Custo de registro
          </Text>
          <Text
            color={'neutral.black'}
            variant="regular"
            isLoading={isLoadingCostSimulator}
            fontSize={Typography['fontsize.md']}
            {...(isLoadingCostSimulator && {
              maxW: '80px',
            })}>
            {Utils.Mask.Money(costSimuatorData?.costRegistration || 0)}
          </Text>
        </HStack>
        <HStack alignItems={'center'} justifyContent={'space-between'}>
          <Text color={'neutral.black'} variant="regular" fontSize={Typography['fontsize.md']}>
            Custo de permanência
          </Text>
          <Text
            color={'neutral.black'}
            variant="regular"
            isLoading={isLoadingCostSimulator}
            fontSize={Typography['fontsize.md']}
            {...(isLoadingCostSimulator && {
              maxW: '80px',
            })}>
            {Utils.Mask.Money(costSimuatorData?.permanenceCost || 0)}
          </Text>
        </HStack>
        <HStack alignItems={'center'} justifyContent={'space-between'}>
          <Text color={'neutral.black'} variant="regular" fontSize={Typography['fontsize.md']}>
            Corretagem no vencimento
          </Text>
          <Text
            color={'neutral.black'}
            variant="regular"
            isLoading={isLoadingCostSimulator}
            fontSize={Typography['fontsize.md']}
            {...(isLoadingCostSimulator && {
              maxW: '80px',
            })}>
            {Utils.Mask.Money(costSimuatorData?.brokerageExpiration || 0)}
          </Text>
        </HStack>
      </VStack>
      <Box w={'full'} bg={'neutral.lightest'} h={'0.5'} />
      <HStack alignItems={'center'} justifyContent={'space-between'}>
        <Text color={'neutral.black'} variant="regular" fontSize={Typography['fontsize.md']}>
          Custo total estimado
        </Text>
        <Text
          color={'neutral.black'}
          variant="regular"
          isLoading={isLoadingCostSimulator}
          fontSize={Typography['fontsize.md']}
          {...(isLoadingCostSimulator && {
            maxW: '80px',
          })}>
          {Utils.Mask.Money(costSimuatorData?.totalCost || 0)}
        </Text>
      </HStack>
      <Button
        variant={'callToAction'}
        isDisabled={
          typeof hasErrorInput?.message === 'string' ||
          !marketIsOpened ||
          isFocused ||
          isLoadingCostSimulator
        }
        onPress={handleSubmit(handleSubmitItem)}>
        Confirmar
      </Button>
    </VStack>
  );
};
